import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Discount {
  id: string;
  name: string;
  type: "category" | "product" | "volume" | "time_based";
  value: number;
  valueType: "percentage" | "fixed";
  applicableTo: string;
  conditions: string;
  priority: number;
  status: "active" | "inactive";
  usageCount: number;
}

const MOCK_DISCOUNTS: Discount[] = [
  {
    id: "1",
    name: "Descuento Ropa de Verano",
    type: "category",
    value: 30,
    valueType: "percentage",
    applicableTo: "Categoría: Ropa de Verano",
    conditions: "Sin mínimo de compra",
    priority: 1,
    status: "active",
    usageCount: 456,
  },
  {
    id: "2",
    name: "Descuento por Volumen",
    type: "volume",
    value: 15,
    valueType: "percentage",
    applicableTo: "Todos los productos",
    conditions: "Mínimo 5 unidades",
    priority: 2,
    status: "active",
    usageCount: 89,
  },
  {
    id: "3",
    name: "Happy Hour",
    type: "time_based",
    value: 20,
    valueType: "percentage",
    applicableTo: "Bebidas y Snacks",
    conditions: "Lunes a Viernes 14:00-16:00",
    priority: 3,
    status: "active",
    usageCount: 234,
  },
  {
    id: "4",
    name: "Producto Especial",
    type: "product",
    value: 50,
    valueType: "fixed",
    applicableTo: "SKU: PROD-12345",
    conditions: "Máximo 2 por cliente",
    priority: 4,
    status: "inactive",
    usageCount: 12,
  },
];

export default function DiscountsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const getTypeIcon = (type: Discount["type"]) => {
    const icons: Record<Discount["type"], string> = {
      category: "ri-folder-line",
      product: "ri-price-tag-3-line",
      volume: "ri-stack-line",
      time_based: "ri-time-line",
    };
    return icons[type];
  };

  const getTypeLabel = (type: Discount["type"]) => {
    const labels: Record<Discount["type"], string> = {
      category: t("posDiscounts.typeCategory"),
      product: t("posDiscounts.typeProduct"),
      volume: t("posDiscounts.typeVolume"),
      time_based: t("posDiscounts.typeTimeBased"),
    };
    return labels[type];
  };

  const getTypeColor = (type: Discount["type"]) => {
    const colors: Record<Discount["type"], string> = {
      category: "bg-blue-500/20 text-blue-500",
      product: "bg-purple-500/20 text-purple-500",
      volume: "bg-amber-500/20 text-amber-500",
      time_based: "bg-pink-500/20 text-pink-500",
    };
    return colors[type];
  };

  const filteredDiscounts = MOCK_DISCOUNTS.filter((discount) => {
    const matchesSearch = discount.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || discount.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = MOCK_DISCOUNTS.filter((d) => d.status === "active").length;
  const totalUsage = MOCK_DISCOUNTS.reduce((acc, d) => acc + d.usageCount, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
            {t("posDiscounts.title")}
          </h1>
          <p className={themeClasses.textSecondary}>
            {t("posDiscounts.subtitle")}
          </p>
        </div>
        <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
          <i className="ri-add-line"></i>
          {t("posDiscounts.createDiscount")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posDiscounts.activeDiscounts")}</span>
            <i className="ri-percent-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{activeCount}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posDiscounts.totalApplied")}</span>
            <i className="ri-checkbox-circle-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalUsage}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posDiscounts.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="all">{t("posDiscounts.allStatuses")}</option>
            <option value="active">{t("posDiscounts.statusActive")}</option>
            <option value="inactive">{t("posDiscounts.statusInactive")}</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.discount")}
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.type")}
                </th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.value")}
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.appliesTo")}
                </th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.priority")}
                </th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.status")}
                </th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posDiscounts.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredDiscounts.map((discount) => (
                <tr key={discount.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{discount.name}</p>
                      <p className={`text-sm ${themeClasses.textSecondary}`}>{discount.conditions}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${getTypeColor(discount.type)}`}>
                      <i className={getTypeIcon(discount.type)}></i>
                      {getTypeLabel(discount.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-primary-500 font-bold">
                      {discount.valueType === "percentage" ? `${discount.value}%` : `$${discount.value}`}
                    </span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.textSecondary} text-sm`}>
                    {discount.applicableTo}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${themeClasses.cardBg} ${themeClasses.text}`}>
                      {discount.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discount.status === "active"}
                        onChange={() => {}}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-edit-line"></i>
                      </button>
                      <button aria-label={t("general.copy", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-file-copy-line"></i>
                      </button>
                      <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
